import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  FestivalListRequest,
  FestivalListResponse,
  FestivalUploadState,
  SaveFestivalPayload,
  UpdateFestivalPayload,
  uploadFilesProps,
} from '../../../types/Settings/FestivalManagement/FestivalManagementType'
import { FestivalUploadApi } from '../../../middleware/api/FestivalManagement/FestivalUploadApi'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'

const uploadFestival = createAsyncThunk(
  'festivalUpload/uploadFestival',
  async (data: uploadFilesProps, thunkApi) => {
    try {
      return await FestivalUploadApi.uploadFestival(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllFestivals = createAsyncThunk(
  'festivalUpload/getAllFestivals',
  async (data: FestivalListRequest, thunkApi) => {
    try {
      return await FestivalUploadApi.getAllFestivals(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const saveFestival = createAsyncThunk(
  'festivalUpload/saveFestival',
  async (data: SaveFestivalPayload, thunkApi) => {
    try {
      return await FestivalUploadApi.saveFestival(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateFestival = createAsyncThunk(
  'festivalUpload/updateFestival',
  async (data: UpdateFestivalPayload, thunkApi) => {
    try {
      return await FestivalUploadApi.updateFestival(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteFestival = createAsyncThunk(
  'festivalUpload/deleteFestival',
  async (festivalId: number, thunkApi) => {
    try {
      return await FestivalUploadApi.deleteFestival(festivalId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const checkFestivalNameExist = createAsyncThunk(
  'festivalUpload/checkFestivalNameExist',
  async (festivalName: string, thunkApi) => {
    try {
      return await FestivalUploadApi.checkFestivalNameExist(festivalName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialFestivalUploadState: FestivalUploadState = {
  isLoading: ApiLoadingState.idle,
  uploadFestivalResponse: null,
  listSize: 0,
  festivalListResponse: {} as FestivalListResponse,
  isLoadingUpload: false,
  isDelete: false,
}

const festivalUploadSlice = createSlice({
  name: 'festivalUpload',
  initialState: initialFestivalUploadState,
  reducers: {
    setIsLoadingUpload: (state, action) => {
      state.isLoadingUpload = action.payload
    },
    setIsDelete: (state, action) => {
      state.isDelete = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAllFestivals.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.festivalListResponse = action.payload
        state.listSize = action.payload?.size || 0
      })
      .addCase(getAllFestivals.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getAllFestivals.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(uploadFestival.fulfilled, (state, action) => {
        state.uploadFestivalResponse = action.payload
        state.isLoadingUpload = false
      })
      .addCase(uploadFestival.pending, (state) => {
        state.isLoadingUpload = true
      })
      .addCase(uploadFestival.rejected, (state) => {
        state.isLoadingUpload = false
      })
      .addCase(saveFestival.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(saveFestival.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(saveFestival.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(updateFestival.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(updateFestival.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(updateFestival.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(deleteFestival.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(deleteFestival.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(deleteFestival.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.festivalUpload.isLoading

const listSize = (state: RootState): number => state.festivalUpload.listSize

const isLoadingUpload = (state: RootState): boolean =>
  state.festivalUpload.isLoadingUpload

const getFestivalListResponse = (state: RootState): FestivalListResponse =>
  state.festivalUpload.festivalListResponse

const getIsDelete = (state: RootState): boolean => state.festivalUpload.isDelete

const festivalUploadThunk = {
  uploadFestival,
  getAllFestivals,
  saveFestival,
  updateFestival,
  deleteFestival,
  checkFestivalNameExist,
}

const festivalUploadSelectors = {
  isLoading,
  listSize,
  isLoadingUpload,
  getFestivalListResponse,
  getIsDelete,
}

export const festivalUploadService = {
  ...festivalUploadThunk,
  actions: festivalUploadSlice.actions,
  selectors: festivalUploadSelectors,
}

export default festivalUploadSlice.reducer
